import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { formatDateForDisplay } from 'app/shared/utils/date';
import { Badge } from 'app/shared/badge';
import { useSenderIDService } from 'hooks/messaging/sender-id';
import { SenderIDRequestForm } from '../form';
import GmModal from 'app/shared/modal/modal';
import { Button } from 'app/shared/button';
import { setPageTitle } from 'store/actions/header';

export const SenderIDListPage = () => {
  const dispatch = useDispatch();
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const { fetchSenderIds, searchSenderId } = useSenderIDService();

  const [sender_ids, setSenderIds] = useState({});
  const [loading, setLoading] = useState(false);
  const [total_sender_ids, setTotalSenderIds] = useState(0);
  const [show_sender_id_modal, setShowSenderIdModal] = useState(false);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Sender IDs', path: '.' }]));
  }, []);

  const config = {
    actions: {},
    css: {},
    fields: [
      {
        title: 'Sender ID',
        key: 'sender_id',
        isTitle: true
      },
      {
        title: 'Company Name',
        key: 'company_name'
      },
      {
        title: 'Use Case',
        key: 'use_case'
      },
      {
        title: 'Time sent',
        key: 'time_stamp',
        isMetadata: true,
        formatter: (value) => formatDateForDisplay(value)
      },
      {
        title: 'Status',
        key: 'status',
        formatter: (value) => (
          <Badge
            type={value === 'rejected' ? 'red' : value === 'pending' ? 'orange' : 'green'}
            text={value}
          />
        )
      }
    ],
    items: Object.values(sender_ids).sort((a, b) => b.time_stamp - a.time_stamp),
    num_of_rows: 10,
    search_key: 'sender_id, company_name',
    search_text: ''
  };

  const handleDataRequest = async (page, population = 10) => {
    if (loading) return;
    setLoading(true);

    const { sender_ids: s_ids, size } = await fetchSenderIds({
      query_string: `page=${page}&population=${population}`
    });

    // Server-paginated table: every page replaces the current rows.
    setSenderIds(() => s_ids.reduce((s, a) => ({ ...s, [a.time_stamp]: a }), {}));
    setTotalSenderIds(() => size);

    setLoading(false);
  };

  const handleSearchRequest = async (keys, keyword, page, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { sender_ids, size } = await searchSenderId(keys, keyword, {
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });

      setTotalSenderIds(size);
      setSenderIds(() => sender_ids.reduce((s, a) => ({ ...s, [a.time_stamp]: a }), {}));
    } catch (e) {
      setSenderIds({});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = (payload) => {
    setSenderIds((ids) => ({
      [payload.time_stamp]: payload,
      ...ids
    }));
  };

  const table_actions = (
    <>
      <Button icon_name="add" text="Request Sender ID" onClick={() => setShowSenderIdModal(true)} />
    </>
  );

  return (
    <GridRow>
      <GridColumn span={4}>
        {is_mobile_view ? (
          <MobileDatatable
            config={config}
            action={() => {}}
            onClick={() => {}}
            onListModeChange={() => {}}
            onDataRequest={handleDataRequest}
            onSearchRequest={() => {}}
            showHeader
          />
        ) : (
          <WebDatatable
            config={{
              ...config,
              total_count: total_sender_ids
            }}
            action={() => {}}
            onClick={() => {}}
            checkbox
            loading_data={loading}
            table_actions={table_actions}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
          />
        )}
        <GmModal
          show_modal={show_sender_id_modal}
          show_title
          title="Request new sender ID"
          onClose={() => setShowSenderIdModal(false)}
        >
          <SenderIDRequestForm onSave={handleSave} />
        </GmModal>
      </GridColumn>
    </GridRow>
  );
};
